<template>
    <div v-if="loading" class="flex min-h-80 items-center justify-center"><Loader /></div>
    <div v-else class="p-4 ds-exchange-panel-content">
        <div class="ds-exchange-panel-title pb-5">Создание вакансии</div>
        <div class="ds-exchange-panel-section border-grayxlight border-b mb-2">Основные данные</div>

        <div class="mb-3">
                <div class="mb-3">
                    <div class="ds-exchange-field-label">Магазин</div>
                    <dropdown :model-value="department ?? undefined" :options="[]" direction="down" :disabled="true">
                        {{ department ? scheduleStore.cutStr(department.name, 23) : '' }}
                    </dropdown>
                    <div v-if="!department" class="text-red text-xs mt-1" role="alert">{{ departmentMissingMessage }}</div>
                </div>

                <div class="flex">
                    <div>
                        <div class="mr-5 mb-2 flex-1">
                            <div class="ds-exchange-field-label">Дата</div>
                            <AppDateInput :model-value="date" :min-date="today" @update:model-value="onDateSelect" />
                        </div>   
                    </div>

                    <div class="mr-5 mb-3">
                        <div class="ds-exchange-field-label">Время начала</div>
                        <time-picker v-model="start" />
                    </div>
                    <div class="mr-5 mb-3">
                        <div class="ds-exchange-field-label">Время окончания</div>
                        <time-picker v-model="end" />
                    </div>
                </div>

                <div class="mb-3 flex-1">
                    <div class="ds-exchange-field-label">Должность</div>
                    <dropdown v-model="position" :options="positions" class="text-xs">
                        {{ position.name }}
                    </dropdown>
                </div>

                 <base-input v-model="price" max-length="5" class="mb-2" @input="onInputDigits">Ставка в час</base-input>
        </div>

        <div class="mb-3" v-if="showExchangeType">
            <div class="ds-exchange-field-label">Тип биржи</div>
            <checkbox class="ds-exchange-checkbox" v-model="inner">Внутренняя</checkbox>
            <checkbox class="ds-exchange-checkbox" v-model="outer">Внешняя</checkbox>
        </div>

        <Skills :skills v-if="skills.length" />

        <div class="ds-exchange-field-label">Комментарий</div>
        <textarea v-model="comment" class="ds-exchange-textarea w-full mb-8" maxlength="100"></textarea>

        <VacancyCopy :min-date="today" @selected="onCopy" class="mb-5" />

        <div class="pt-3 text-right">
            <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
            <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Data {
    date: string,
    dttm_work_start_at: string,
    dttm_work_end_at: string,
    department: string,
    position: string,
    comment?: string,
    salary_hour?: string,
    inner_exchange?: boolean,
    outer_exchange?: boolean
}
import VacancyCopy from './VacancyCopy.vue'
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { Option } from '@/types'
import dayjs, { type Dayjs } from 'dayjs'
import utc from "dayjs/plugin/utc"
import isBetween from "dayjs/plugin/isBetween"
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Loader from '@/ui/Loader.vue'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import Skills from './Skills.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useExchangeStore } from '@/stores/exchangeStore'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from 'vue-toast-notification'

const scheduleStore = useScheduleStore()
const exchangeStore = useExchangeStore()
const authStore = useAuthStore()
const toast = useToast({ position: 'bottom-right' })
const pastStartMessage = 'Дата и время начала вакансии не могут быть раньше текущего времени'
const departmentMissingMessage = 'Не удалось определить магазин пользователя'
interface Props {
    initialValues?: {
        date: string
        department: Option
        inner: boolean
        outer: boolean
    }
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'saved'])
dayjs.extend(utc)
dayjs.extend(isBetween)
dayjs.extend(customParseFormat)
const positions = computed(() => [{id: "", name: 'Выберите должность'}, ...scheduleStore.positions])
const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))

const loading = ref(false)
const openedAt = dayjs()
const initialDate = props.initialValues
    ? dayjs(props.initialValues.date, 'YYYY-MM-DD', true)
    : openedAt.startOf('day')
const date = ref<Dayjs | null>(initialDate.isValid() ? initialDate : null)
const start = ref<string | null>('09:00')
const end = ref<string | null>('18:00')
if (date.value?.isSame(openedAt, 'day')) {
    const lastMinute = openedAt.startOf('day').hour(23).minute(59)
    const initialStart = dayjs(Math.min(openedAt.add(15, 'minute').valueOf(), lastMinute.valueOf()))
    const initialEnd = dayjs(Math.min(initialStart.add(1, 'hour').valueOf(), lastMinute.valueOf()))
    start.value = initialStart.format('HH:mm')
    end.value = initialEnd.format('HH:mm')
}
const now = ref(openedAt)
const today = computed(() => now.value.startOf('day'))
const saving = ref(false)
const comment = ref('')
const isDisabled = ref(true)
const position = ref<Option>(positions.value[0])
// Магазин назначения — только из профиля; initialValues.department и список своих магазинов не используются.
const department = computed<Option | null>(() => {
    const profileDepartment = authStore.userInfo?.department
    return profileDepartment?.id ? { id: profileDepartment.id, name: profileDepartment.name ?? '' } : null
})
const price = ref('')
const skills = ref<any[]>([])
const inner = ref(props.initialValues?.inner ?? true)
const outer = ref(props.initialValues?.outer ?? false)
const copyDates = ref<[Dayjs, Dayjs] | null>(null)
let refreshInterval: ReturnType<typeof setInterval> | undefined

const isTimeValid = (value: string | null): value is string =>
    typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)

const isDateValid = (value: Dayjs | null | undefined): value is Dayjs =>
    dayjs.isDayjs(value) && value.isValid()

const isStartPast = (day: Dayjs, time: string, currentTime: Dayjs) => {
    const [hours, minutes] = time.split(':').map(Number)
    // Проверяем локальное время отдельно от существующей API-строки с буквальным Z.
    const localStart = new Date(day.year(), day.month(), day.date(), hours, minutes, 0, 0)
    return localStart.getTime() < currentTime.valueOf()
}

const checkDates = (currentTime: Dayjs): 'invalid' | 'past' | null => {
    if (!isDateValid(date.value) || !isTimeValid(start.value) || !isTimeValid(end.value)) return 'invalid'
    if (start.value >= end.value) return 'invalid'
    let past = isStartPast(date.value, start.value, currentTime)
    if (copyDates.value) {
        const [from, to] = copyDates.value
        if (!isDateValid(from) || !isDateValid(to) || from.isAfter(to, 'day')) return 'invalid'
        for (let day = from.startOf('day'); !day.isAfter(to, 'day'); day = day.add(1, 'day')) {
            if (isStartPast(day, start.value, currentTime)) past = true
        }
    }
    return past ? 'past' : null
}

const validation = () => {
    now.value = dayjs()
    const dateError = checkDates(now.value)
    isDisabled.value = saving.value || !department.value || dateError === 'invalid' ||
        !((!price.value || price.value.length > 1) && position.value.id && (inner.value || outer.value))
    return dateError
}

const onDateSelect = (value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) => {
    date.value = dayjs.isDayjs(value) ? value : null
}

const onInputDigits = () => {
    price.value = price.value.replace(/\D/g, '');
}

const cancel = () => {
    emit('close')
}

const save = async () => {   
    if (saving.value) return
    const dateError = validation()
    if (dateError === 'past') {
        toast.error(pastStartMessage)
        return
    }
    if (!department.value) {
        toast.error(departmentMissingMessage)
        return
    }
    if (isDisabled.value || !isDateValid(date.value) || !isTimeValid(start.value) || !isTimeValid(end.value)) return
    saving.value = true
    isDisabled.value = true     
    const mainDate = date.value
    const startTime = start.value
    const hasCopyDates = copyDates.value !== null
    const formatDate = dayjs(date.value).format('YYYY-MM-DD')
    let inRange = false
    const data:Data = {
        date: formatDate,
        dttm_work_start_at: `${formatDate}T${start.value}:00.000Z`,
        dttm_work_end_at: `${formatDate}T${end.value}:00.000Z`,
        department: department.value.id,
        position: position.value.id,
        inner_exchange: inner.value,
        outer_exchange: outer.value
    }
    if (price.value) data.salary_hour = price.value
    if (comment.value) data.comment = comment.value
    let isSaved = false
    try {
        if(copyDates.value) {
            // Профиль мог обновиться за время сохранения — ID проверяется перед каждым POST.
            if (!department.value) {
                toast.error(departmentMissingMessage)
                return
            }
            const copyData = {
                department: department.value.id,
                position: position.value.id,
                date_from: dayjs(copyDates.value[0]).format('YYYY-MM-DD'),
                date_to: dayjs(copyDates.value[1]).format('YYYY-MM-DD'),
                work_start_time: start.value,
                work_end_time: end.value,
                inner_exchange: inner.value,
                outer_exchange: outer.value
            }
            const result = await exchangeStore.addVacancyPack(copyData)
            isSaved = Boolean(result?.data && result.data.status !== 'error')
            // Проверка даты на вхождение в промежуток
            inRange = dayjs(data.date).isBetween(copyData.date_from, copyData.date_to, 'day',  '[]')
        }
        // Если не входит в промежуток то создаем вакансию
        if(!inRange) {
            if (hasCopyDates && isStartPast(mainDate, startTime, dayjs())) {
                if (isSaved) {
                    emit('saved')
                    toast.error('Пакет вакансий создан. Основная вакансия не создана: дата и время её начала раньше текущего времени')
                } else {
                    toast.error(pastStartMessage)
                }
                return
            }
            if (!department.value) {
                if (isSaved) emit('saved')
                toast.error(departmentMissingMessage)
                return
            }
            data.department = department.value.id
            const result = await exchangeStore.addVacancy(data)
            isSaved = Boolean(result?.data && result.data.status !== 'error')
        }
        
        if (isSaved) emit('saved')
        emit('close')
        position.value = positions.value[0]
    } finally {
        saving.value = false
        validation()
    }
}

const onCopy = (dates: [Dayjs, Dayjs]) => {
    copyDates.value = dates
}

onMounted(() => {
    validation()
    refreshInterval = setInterval(() => validation(), 60_000)
})

onUnmounted(() => clearInterval(refreshInterval))

watch([date, price, start, end, position, inner, outer, copyDates, department], () => { validation() })

watch(position, () => {
    skills.value = scheduleStore.positions.find(item => item.id === position.value.id)?.skills || []
})

</script>
